import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Server-enforced drip gate around a static preview page.
 *
 * On mount: calls GET /api/curriculum/gate/:slug and either renders the
 * lesson content (children) with a NextLessonButton appended, or a locked
 * screen if the server says the current lesson is not unlocked yet.
 *
 * If the user is signed out, redirects to /login.
 * If the user is not enrolled or is refunded/paused, the request 401s and
 * we send them to /login as well.
 */

export type GateNext = {
  slug: string;
  title: string;
  weekNumber: number;
  unlocked: boolean;
  unlockAt: string | null;
  crossesWeek: boolean;
};

type GateResponse = {
  unlocked: boolean;
  unlockAt: string | null;
  weekNumber: number;
  moduleTitle: string;
  title: string;
  next: GateNext | null;
};

export default function LessonGate({
  slug,
  children,
}: {
  slug: string;
  children: (ctx: { next: GateNext | null; onComplete: () => Promise<void>; completedAt: string | null }) => ReactNode;
}) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'unauthed' }
    | { kind: 'locked'; data: GateResponse }
    | { kind: 'ok'; data: GateResponse }
    | { kind: 'error' }
  >({ kind: 'loading' });
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const res = await api.get<GateResponse>(`/api/curriculum/gate/${slug}`);
        if (!res.unlocked) {
          setState({ kind: 'locked', data: res });
        } else {
          setState({ kind: 'ok', data: res });
          // Best-effort: also fetch progress for the completed indicator
          try {
            const prog = await api.get<{ progress: Array<{ lessonId: string; completedAt: string | null }> }>(
              '/api/curriculum/progress'
            );
            // We match by slug via the lesson endpoint (cheap because we already know it's unlocked)
            const lessonRes = await api.get<{ lesson: { id: string }; completedAt: string | null }>(
              `/api/curriculum/lesson/${slug}`
            );
            const matched = prog.progress.find((p) => p.lessonId === lessonRes.lesson.id);
            setCompletedAt(matched?.completedAt ?? lessonRes.completedAt ?? null);
          } catch {
            // non-fatal
          }
        }
      } catch (err) {
        const status = (err as { status?: number })?.status;
        const code = (err as { error?: string })?.error;
        if (status === 401 || code === 'unauthorized') {
          setState({ kind: 'unauthed' });
        } else {
          setState({ kind: 'error' });
        }
      }
    })();
  }, [slug]);

  async function onComplete() {
    try {
      const res = await api.post<{ ok: true; completedAt: string }>(
        `/api/curriculum/lesson/${slug}/complete`
      );
      setCompletedAt(res.completedAt);
    } catch {
      // ignore
    }
  }

  if (state.kind === 'loading') {
    return <div className="mx-auto max-w-2xl py-16 text-muted">Loading…</div>;
  }
  if (state.kind === 'unauthed') {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (state.kind === 'error') {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <h1 className="text-2xl font-semibold">Something went wrong.</h1>
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-amber hover:underline">
          ← Back to dashboard
        </Link>
      </div>
    );
  }
  if (state.kind === 'locked') {
    return <LockedScreen data={state.data} />;
  }

  // OK: render the lesson content, with next-button + progress helpers.
  return <>{children({ next: state.data.next, onComplete, completedAt })}</>;
}

function LockedScreen({ data }: { data: GateResponse }) {
  const unlockDate = data.unlockAt ? new Date(data.unlockAt) : null;
  const countdown = useCountdown(unlockDate);

  return (
    <div className="mx-auto max-w-2xl px-6 py-20">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week {data.weekNumber} · {data.moduleTitle}
      </p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-text">
        This lesson is still locked.
      </h1>
      <p className="mt-4 text-muted">
        {unlockDate
          ? `Week ${data.weekNumber} unlocks in ${countdown} on ${unlockDate.toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}.`
          : 'Your enrollment cadence has not reached this week yet.'}
      </p>
      <p className="mt-2 text-sm text-muted">
        One week releases every seven days from your enrollment date. You can revisit any week you have already unlocked.
      </p>
      <Link
        to="/dashboard"
        className="mt-8 inline-block rounded-md border border-line px-4 py-2 text-sm text-text hover:border-amber"
      >
        ← Back to dashboard
      </Link>
    </div>
  );
}

function useCountdown(target: Date | null): string {
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return '';
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return 'less than a minute';
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} ${hours} hour${hours === 1 ? '' : 's'}`;
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return `${hours} hour${hours === 1 ? '' : 's'} ${mins} minute${mins === 1 ? '' : 's'}`;
}
